/* =======================================================================
   CART DRAWER REPAIR
   Loaded from snippets/unified-offer.liquid. Standalone - it does not know
   anything about the offer and never touches assets/rotating-offer.js.

   THE BUG
   -------
   Press "Proceed with Offer" and the drawer sometimes opens reading "your
   cart is empty", while the cart icon shows the right count. The cart on
   the server is correct; only the HTML on screen is wrong. There is no way
   out of it from the storefront, which is what makes it costly: the
   shopper concludes the order failed.

   WHY IT HAPPENS
   --------------
   rotating-offer.js adds the items, then asks Shopify for fresh cart
   sections. Discount Ninja reacts to that add by clearing the cart and
   rebuilding it so it can apply the discount - measured at roughly 2.8s of
   churn. If the section snapshot is taken during that window, the HTML
   Shopify returns genuinely IS the empty state.

   The engine already guards against this (fetchCartSectionsSettled retries
   while the snapshot says empty) but the guard is capped at about 700ms,
   far short of the 2.8s it would need. And once the drawer is open,
   applyCartSections() deliberately refuses to re-render it - that refusal
   is what stops the drawer being swapped out mid-open and stranding
   body.overflow-hidden. So a late snapshot is never corrected, and the
   empty render sticks for the rest of the page view.

   THE FIX
   -------
   Repair after the fact instead of racing. Whenever the drawer is open and
   claims to be empty, check the real cart; if it has items, pull a fresh
   section and swap the drawer's CONTENTS in.

   Swapping innerHTML rather than replacing <cart-drawer> is deliberate and
   load-bearing: replacing the element drops the open state and leaves
   body.overflow-hidden behind, which is the "page is frozen and I cannot
   scroll" bug.
   ======================================================================= */

(function () {
  'use strict';

  if (window.__cartDrawerRepairBound) return;
  window.__cartDrawerRepairBound = true;

  var MAX_STALE_RETRIES = 5;   // section itself still reporting empty
  var STALE_BACKOFF_MS = 700;

  function drawerEl() {
    return document.querySelector('cart-drawer');
  }

  function isOpen(d) {
    return !!d && (d.classList.contains('active') ||
                   d.classList.contains('animate') ||
                   d.hasAttribute('open'));
  }

  function repair(attempt) {
    attempt = attempt || 0;
    var d = drawerEl();

    // Only ever touch a drawer the shopper is looking at that says it is
    // empty. Before it opens it is legitimately empty, and firing then just
    // puts needless requests on the wire during the add.
    if (!d || !isOpen(d) || !d.classList.contains('is-empty')) return;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.item_count) return;   // genuinely empty - correct as rendered

        return fetch('/?sections=cart-drawer')
          .then(function (r) { return r.json(); })
          .then(function (map) {
            var html = map && map['cart-drawer'];
            if (!html) return;

            var fresh = new DOMParser()
              .parseFromString(html, 'text/html')
              .querySelector('cart-drawer');
            if (!fresh) return;

            // Shopify can still be serving the mid-rebuild cart. Back off
            // and ask again rather than painting the same empty state back.
            if (fresh.classList.contains('is-empty')) {
              if (attempt < MAX_STALE_RETRIES) {
                setTimeout(function () { repair(attempt + 1); }, STALE_BACKOFF_MS);
              }
              return;
            }

            var live = drawerEl();
            if (!live || !isOpen(live)) return;   // closed while we were fetching

            live.innerHTML = fresh.innerHTML;
            live.classList.remove('is-empty');
          });
      })
      .catch(function () {});
  }

  // A ladder rather than a single shot: Discount Ninja's rebuild finishes at
  // an unpredictable point, so keep looking until it has settled.
  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    [0, 700, 1800, 3500, 6000].forEach(function (ms) {
      setTimeout(function () { repair(); }, ms);
    });
    setTimeout(function () { queued = false; }, 6500);
  }

  // Watch the SECTION, not <cart-drawer>. applyCartSections() rebuilds the
  // section's innerHTML, which destroys and recreates the drawer element - an
  // observer bound to the element ends up watching a detached node and
  // silently never fires again.
  var host = document.getElementById('shopify-section-cart-drawer') || document.body;
  new MutationObserver(function () {
    if (isOpen(drawerEl())) schedule();
  }).observe(host, {
    childList: true, subtree: true,
    attributes: true, attributeFilter: ['class', 'open']
  });

  // Re-opening the cart is the shopper's own instinct when it looks wrong,
  // so make that a recovery path too.
  document.addEventListener('cart:updated', schedule);
  document.addEventListener('la:dn:drawercart:opened', schedule);
  document.addEventListener('la:dn:cart:updated', schedule);

  // and in case the drawer was already open when this loaded
  if (isOpen(drawerEl())) schedule();
})();
